import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SEO from "../components/SEO";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ stage: "checking" });
  const { clear } = useCart();
  const { t } = useI18n();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ stage: "missing" });
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setStatus({ stage: "paid", data });
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus({ stage: "expired", data });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setStatus({ stage: "timeout", data });
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts.current += 1;
        if (attempts.current >= 5) {
          setStatus({ stage: "error" });
          return;
        }
        timer = setTimeout(poll, 2500);
      }
    };

    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <>
      <SEO title={t("payment.seo_title")} description={t("payment.seo_description")} />
      <section className="min-h-[70vh] bg-white py-24" data-testid="payment-success-page">
        <div className="max-w-2xl mx-auto px-4 text-center">
          {status.stage === "checking" && (
            <>
              <Clock className="w-14 h-14 text-[#1a5193] mx-auto animate-pulse" />
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-6">
                {t("payment.checking_title")}
              </h1>
              <p className="text-gray-600 mt-3">{t("payment.checking_sub")}</p>
            </>
          )}
          {status.stage === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-[#25D366] mx-auto" data-testid="payment-success-icon" />
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-6">
                {t("payment.paid_title")}
              </h1>
              <p className="text-gray-600 mt-3">{t("payment.paid_sub")}</p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Button
                  asChild
                  className="bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-xs"
                >
                  <Link to="/" data-testid="success-home-link">{t("payment.back_home")}</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-xl font-chivo font-bold uppercase text-xs"
                >
                  <Link to="/webshop" data-testid="success-shop-link">{t("payment.continue_shopping")}</Link>
                </Button>
              </div>
            </>
          )}
          {(status.stage === "expired" || status.stage === "error" || status.stage === "missing" || status.stage === "timeout") && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto" />
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-6">
                {status.stage === "expired" ? t("payment.expired_title") : t("payment.error_title")}
              </h1>
              <p className="text-gray-600 mt-3">
                {status.stage === "missing" ? t("payment.missing_sub") : t("payment.error_sub")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Button asChild className="bg-[#082c50] hover:bg-[#082c50] rounded-xl font-chivo font-bold uppercase text-xs">
                  <Link to="/webshop" data-testid="error-shop-link">{t("payment.back_webshop")}</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xl font-chivo font-bold uppercase text-xs">
                  <a href="tel:+31488235999" data-testid="error-contact-link">Bel +31 (0) 488 23 5999</a>
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
